// src/components/Products.js
// AI products Isabella built end-to-end.
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Reveal, TiltCard, staggerContainer, staggerItem } from './ui/Motion';
import { IconChef, IconTarget, IconChart, IconStethoscope } from './ui/Icons';
import { useInView } from 'react-intersection-observer';

const products = [
  {
    name: 'Jikoni',
    tagline: 'Voice AI Cooking Assistant',
    description:
      'Hands-free, conversational recipe guidance powered by OpenAI Realtime Voice — built with Expo React Native and Supabase.',
    stack: ['OpenAI Realtime', 'Expo RN', 'Supabase'],
    accent: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    glow: 'rgba(245, 158, 11, 0.35)',
    Icon: IconChef,
  },
  {
    name: 'DarePredict',
    tagline: 'Social Prediction App',
    description:
      'A social prediction platform with real-time engagement mechanics, analytics dashboards, and user-retention systems.',
    stack: ['React', 'Real-time', 'Analytics'],
    accent: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    glow: 'rgba(99, 102, 241, 0.35)',
    Icon: IconTarget,
  },
  {
    name: 'WhyDidItDie',
    tagline: 'AI Marketing Intelligence',
    description:
      'AI-powered marketing intelligence platform built from scratch: Node.js + Supabase APIs, subscription payments, automated email, and Gemini content generation.',
    stack: ['Node.js', 'Gemini', 'Supabase', 'Paystack'],
    accent: 'linear-gradient(135deg, #06b6d4, #10b981)',
    glow: 'rgba(6, 182, 212, 0.35)',
    Icon: IconChart,
  },
  {
    name: 'JourneyDoctors',
    tagline: 'AI-First Telehealth',
    description:
      'AI-first telehealth platform connecting patients with AI specialist doctors and nurses via chat and voice — with real human provider escalation.',
    stack: ['AI Voice', 'Telehealth', 'React'],
    accent: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
    glow: 'rgba(236, 72, 153, 0.35)',
    Icon: IconStethoscope,
  },
];

const Products = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Section id="products">
      <Container>
        <Reveal>
          <Eyebrow>Built end-to-end</Eyebrow>
          <Title>
            I don't just market products —{' '}
            <span className="grad-animate">I ship them.</span>
          </Title>
          <Lead>
            Four AI-powered products taken from architecture through deployment and
            design. Full-stack, independently shipped: React, Node.js,
            Supabase/Firebase, voice AI, and go-to-market.
          </Lead>
        </Reveal>

        <Grid
          ref={ref}
          as={motion.div}
          variants={staggerContainer(0.12)}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
        >
          {products.map((p) => (
            <motion.div key={p.name} variants={staggerItem}>
              <Card glow={p.glow}>
                <CardInner style={{ '--glow': p.glow }}>
                  <Top>
                    <IconBadge style={{ background: p.accent }}>
                      <p.Icon size={26} />
                    </IconBadge>
                    <Shipped>Shipped</Shipped>
                  </Top>
                  <Name>{p.name}</Name>
                  <TaglineText style={{ backgroundImage: p.accent }}>
                    {p.tagline}
                  </TaglineText>
                  <Desc>{p.description}</Desc>
                  <Stack>
                    {p.stack.map((s) => (
                      <Chip key={s}>{s}</Chip>
                    ))}
                  </Stack>
                </CardInner>
              </Card>
            </motion.div>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

/* ---------------- styles ---------------- */

const Section = styled.section`
  padding: 7rem 0;
  position: relative;
  background: ${({ theme }) => theme.colors.dark};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4.5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #06b6d4;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3.2rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  max-width: 760px;
  margin-bottom: 1.25rem;
`;

const Lead = styled.p`
  max-width: 640px;
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 3.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(TiltCard)`
  height: 100%;
  border-radius: 22px;
`;

const CardInner = styled.div`
  position: relative;
  height: 100%;
  padding: 2rem;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid ${({ theme }) => theme.colors.border};
  backdrop-filter: blur(14px);
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -20%;
    width: 60%;
    height: 80%;
    background: radial-gradient(circle, var(--glow), transparent 70%);
    opacity: 0.5;
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.22);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.4);
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const IconBadge = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  color: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

  svg { display: block; }
`;

const Shipped = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #10b981;
  padding: 0.3rem 0.7rem;
  border-radius: 9999px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.3);

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 8px #10b981;
  }
`;

const Name = styled.h3`
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin-bottom: 0.3rem;
`;

const TaglineText = styled.div`
  display: inline-block;
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 1rem;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const Desc = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const Stack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.span`
  font-size: 0.78rem;
  font-weight: 500;
  padding: 0.35rem 0.75rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export default Products;
